import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Typography, Button, Input, Select, Space,
  Empty, Spin, Tooltip, Popover, Checkbox, Alert,
} from 'antd';
import axios from 'axios';

const { Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ── neutral greys + faint sky-blue accent (+ amber for ON HOLD) ── */
const T = {
  ink: '#1f1f1f', sub: '#595959', muted: '#8c8c8c', faint: '#bfbfbf',
  border: '#d9d9d9', line: '#ececec', hairline: '#f0f0f0',
  panel: '#fafafa', head: '#f5f5f5', white: '#ffffff',
  blue: '#3f74ad', blueHover: '#4f86c6', blueActive: '#356296',
  blueSoft: '#eaf3fc', blueLine: '#d6e6f5',
  // ON HOLD accent
  hold: '#d46b08', holdSoft: '#fff7e6', holdLine: '#ffd591',
};

/* Spreadsheet grid — bordered, tight, single-line cells */
const GRID_CSS = `
  .pts-subs .ant-table { font-size: 12px; }
  .pts-subs .ant-table-cell {
    padding: 6px 10px !important;
    border-color: ${T.line} !important;
    white-space: nowrap;
  }
  .pts-subs .ant-table-thead > tr > th {
    background: ${T.head} !important;
    color: ${T.sub} !important;
    font-weight: 600 !important;
    border-color: ${T.line} !important;
    padding: 7px 10px !important;
  }
  .pts-subs .ant-table-tbody > tr > td { color: ${T.ink}; }
  .pts-subs .ant-table-tbody > tr:hover > td { background: ${T.blueSoft} !important; }
  .pts-subs .ant-table-row-rejected > td { background: #fbfbfb; }
  /* ON HOLD rows — faint amber with a left marker */
  .pts-subs .ant-table-row-hold > td { background: #fffdf5; }
  .pts-subs .ant-table-row-hold:hover > td { background: ${T.holdSoft} !important; }
  .pts-subs .ant-table-row-hold > td:first-child { box-shadow: inset 3px 0 0 ${T.hold}; }
`;

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (val) => {
  if (!val) return '-';
  return new Date(val).toLocaleString('en-GB', {
    timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const CATEGORY_LABELS = {
  material: 'Materials', labor_supply: 'Labor Supply', sub_contractor: 'Sub-Contractors',
  heavy_equipment_rental: 'Heavy Equip.', equipment_rental: 'Equipment',
  fees_and_charges: 'Fees & Charges', repairs_and_maintenance: 'Repairs & Maint.',
};

const STATUS_LABELS = {
  pending_review: 'Pending Review',
  under_review: 'Under Review',
  on_hold: 'On Hold',
  approved: 'Approved',
  rejected: 'Rejected',
};

/* Plain neutral status cell — rejected keeps the blue accent, on hold gets amber */
const StatusCell = ({ status }) => {
  const label = STATUS_LABELS[status] || status || '-';
  const isRejected = status === 'rejected';
  const isHold = status === 'on_hold';
  const borderColor = isHold ? T.holdLine : (isRejected ? T.blue : T.border);
  const textColor = isHold ? T.hold : (isRejected ? T.blue : T.ink);
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 2,
      border: `1px solid ${borderColor}`,
      background: isHold ? T.holdSoft : 'transparent',
      fontSize: 11, color: textColor,
      fontWeight: (isRejected || isHold) ? 600 : 500, lineHeight: '16px',
    }}>{label}</span>
  );
};

const cell = (t, opts = {}) => (
  <span style={{ fontSize: 12, color: opts.muted ? T.muted : T.ink, fontWeight: opts.strong ? 600 : 400 }}>{t || '—'}</span>
);

const SubmissionsList = ({ username, isAccounts, viewAll, onViewDetail, statusFilter }) => {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(statusFilter || 'all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBatch, setSelectedBatch] = useState('all');

  // Column visibility — TRN and DN No. hidden by default; user can toggle from the Columns chooser
  const [hiddenKeys, setHiddenKeys] = useState(['trn', 'dn']);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, offset: (page - 1) * pageSize };
      if (!viewAll) params.submitted_by = username;
      if (selectedStatus && selectedStatus !== 'all') params.status = selectedStatus;
      if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedBatch && selectedBatch !== 'all') params.batch_no = selectedBatch;

      const res = await axios.get(`${API_BASE_URL}/api/procurement/submissions`, { params });
      setSubmissions(res.data.submissions || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoading(false);
    }
  }, [username, isAccounts, viewAll, page, pageSize, selectedStatus, selectedCategory, selectedBatch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredData = searchText
    ? submissions.filter((s) =>
        (s.submission_ref || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (s.vendor_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (s.lpo_number || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (s.invoice_number || '').toLowerCase().includes(searchText.toLowerCase()) ||
        String(s.batch_no || '').includes(searchText)
      )
    : submissions;

  const heldOnPage = useMemo(() => filteredData.filter(s => s.status === 'on_hold'), [filteredData]);

  const batchNos = [...new Set(submissions.map(s => s.batch_no).filter(Boolean))].sort((a, b) => a - b);

  const title = statusFilter === 'pending_review'
    ? 'Pending Review'
    : (isAccounts ? 'All Submissions' : 'My Submissions');

  const columns = [
    {
      title: 'Batch', dataIndex: 'batch_no', key: 'batch', width: 64, fixed: 'left',
      sorter: (a, b) => (a.batch_no || 0) - (b.batch_no || 0),
      render: (v) => v ? <Text strong style={{ fontSize: 12, color: T.ink }}>{v}</Text> : '—',
    },
    {
      title: 'Ref', dataIndex: 'submission_ref', key: 'ref', width: 140, fixed: 'left',
      render: (text, record) => (
        <span style={{ color: T.blue, fontWeight: 600, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => onViewDetail(record.id)}>{text}</span>
      ),
    },
    {
      title: 'Category', dataIndex: 'category', key: 'category', width: 120,
      render: (c) => <Text strong style={{ fontSize: 12, color: T.blue }}>{CATEGORY_LABELS[c] || c || '—'}</Text>,
    },
    { title: 'LPO', dataIndex: 'lpo_number', key: 'lpo', width: 80, render: (t) => cell(t, { strong: true }) },
    {
      title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor', width: 190,
      render: (t) => <Tooltip title={t}><span style={{ fontSize: 12, color: t ? T.ink : T.faint, display: 'inline-block', maxWidth: 178, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{t || '—'}</span></Tooltip>,
    },
    { title: 'Invoice No.', dataIndex: 'invoice_number', key: 'inv', width: 120, render: (t) => cell(t) },
    { title: 'Inv. Date', dataIndex: 'invoice_date', key: 'inv_date', width: 96, render: (t) => cell(t, { muted: true }) },
    {
      title: 'Total', dataIndex: 'total_amount', key: 'amount', width: 128, align: 'right',
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
      render: (val) => <Text strong style={{ fontSize: 12, color: T.ink }}>{formatAmount(val)}</Text>,
    },
    { title: 'VAT', dataIndex: 'vat_amount', key: 'vat', width: 104, align: 'right', render: (val) => <span style={{ fontSize: 11, color: T.sub }}>{formatAmount(val)}</span> },
    { title: 'TRN', dataIndex: 'vendor_trn', key: 'trn', width: 130, render: (t) => <span style={{ fontSize: 11, color: t ? T.sub : T.faint }}>{t || '—'}</span> },
    { title: 'DN No.', dataIndex: 'dn_number', key: 'dn', width: 84, render: (t) => cell(t, { muted: true }) },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => <StatusCell status={s} /> },
    {
      title: 'Submitted', dataIndex: 'submitted_at', key: 'date', width: 118,
      render: (val) => {
        if (!val) return '—';
        const d = new Date(val);
        return (
          <div style={{ lineHeight: 1.3 }}>
            <span style={{ fontSize: 11, color: T.sub, display: 'block' }}>{d.toLocaleDateString('en-GB', { timeZone: 'Asia/Dubai' })}</span>
            <span style={{ fontSize: 10, color: T.muted }}>{d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: false })}</span>
          </div>
        );
      },
    },
    ...(isAccounts ? [{
      title: 'By', dataIndex: 'submitted_by', key: 'user', width: 110,
      render: (t) => <span style={{ fontSize: 11, color: T.sub }}>{t ? t.split('@')[0] : '—'}</span>,
    }] : []),
    {
      // Carries the accounts decision AND the auto-generated hold note
      title: 'Remarks', dataIndex: 'accounts_remarks', key: 'remarks', width: 210,
      render: (t, record) => {
        const held = record.status === 'on_hold';
        const text = held ? (record.hold_remarks || t) : t;
        if (!text) return '—';
        return (
          <Tooltip title={text}>
            <span style={{
              fontSize: 11, color: held ? T.hold : T.sub, fontWeight: held ? 600 : 400,
              display: 'inline-block', maxWidth: 198, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle',
            }}>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action', key: 'action', width: 80, fixed: 'right',
      render: (_, record) => (
        <span style={{ color: T.blue, cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }} onClick={() => onViewDetail(record.id)}>View</span>
      ),
    },
  ];

  // Columns the user can show/hide (Batch, Ref and Action stay fixed)
  const TOGGLE_COLS = [
    { key: 'category', label: 'Category' },
    { key: 'lpo', label: 'LPO' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'inv', label: 'Invoice No.' },
    { key: 'inv_date', label: 'Inv. Date' },
    { key: 'amount', label: 'Total' },
    { key: 'vat', label: 'VAT' },
    { key: 'trn', label: 'TRN' },
    { key: 'dn', label: 'DN No.' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Submitted' },
    ...(isAccounts ? [{ key: 'user', label: 'By' }] : []),
    { key: 'remarks', label: 'Remarks' },
  ];
  const checkedCols = TOGGLE_COLS.map(c => c.key).filter(k => !hiddenKeys.includes(k));
  const onColsChange = (vals) => setHiddenKeys(TOGGLE_COLS.map(c => c.key).filter(k => !vals.includes(k)));
  const visibleColumns = columns.filter(c => !hiddenKeys.includes(c.key));

  const columnsChooser = (
    <div style={{ minWidth: 150 }}>
      <Checkbox.Group value={checkedCols} onChange={onColsChange}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {TOGGLE_COLS.map(c => <Checkbox key={c.key} value={c.key}>{c.label}</Checkbox>)}
        </div>
      </Checkbox.Group>
    </div>
  );

  return (
    <div className="pts-subs">
      <style>{GRID_CSS}</style>

      {/* ── ON HOLD notice for the rows currently shown ── */}
      {heldOnPage.length > 0 && (
        <Alert
          type="warning" showIcon
          message={`${heldOnPage.length} invoice${heldOnPage.length > 1 ? 's on this page are' : ' on this page is'} on hold with Accounts`}
          description="Accounts has parked these while they review them. Expand a highlighted row to read the full note — no re-upload or revision is needed."
          style={{ marginBottom: 14, borderRadius: 4 }}
        />
      )}

      <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white }}>
        {/* Header bar */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.hairline}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <Text strong style={{ fontSize: 14, color: T.ink }}>{title}</Text>
            <Text style={{ fontSize: 12, color: T.muted, marginLeft: 10 }}>{total} submissions</Text>
          </div>
          <Space size={8}>
            <Popover content={columnsChooser} trigger="click" placement="bottomRight" title={<span style={{ fontSize: 12, color: T.sub }}>Show columns</span>}>
              <Button style={{ borderRadius: 2 }}>Columns</Button>
            </Popover>
            <Button onClick={fetchData} loading={loading} style={{ borderRadius: 2 }}>Refresh</Button>
          </Space>
        </div>

        {/* Filters */}
        <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.hairline}`, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input placeholder="Search ref, vendor, LPO, invoice, batch…"
            value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear
            style={{ borderRadius: 2, maxWidth: 290, height: 32, fontSize: 12 }} />
          <Select value={selectedStatus} onChange={(val) => { setSelectedStatus(val); setPage(1); }}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'pending_review', label: 'Pending Review' },
              { value: 'on_hold', label: 'On Hold' },
              { value: 'approved', label: 'Approved' },
              { value: 'rejected', label: 'Rejected' },
            ]} />
          <Select value={selectedCategory} onChange={(val) => { setSelectedCategory(val); setPage(1); }}
            style={{ width: 160 }}
            options={[
              { value: 'all', label: 'All Categories' },
              { value: 'material', label: 'Materials' },
              { value: 'labor_supply', label: 'Labor Supply' },
              { value: 'sub_contractor', label: 'Sub-Contractors' },
              { value: 'heavy_equipment_rental', label: 'Heavy Equip.' },
              { value: 'equipment_rental', label: 'Equipment' },
              { value: 'fees_and_charges', label: 'Fees & Charges' },
              { value: 'repairs_and_maintenance', label: 'Repairs' },
            ]} />
          <Select value={selectedBatch} onChange={(val) => { setSelectedBatch(val); setPage(1); }}
            style={{ width: 140 }} showSearch
            filterOption={(input, option) => String(option.label).includes(input)}
            options={[
              { value: 'all', label: 'All Batches' },
              ...batchNos.map(b => ({ value: b, label: `Batch ${b}` })),
            ]} />
          {selectedStatus === 'on_hold' && (
            <Text style={{ fontSize: 12, color: T.hold }}>
              Held by Accounts — the Remarks column carries the reason
            </Text>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 70, textAlign: 'center' }}><Spin /></div>
        ) : filteredData.length === 0 ? (
          <Empty style={{ padding: 56 }} description={<Text type="secondary">No submissions found</Text>} />
        ) : (
          <Table
            columns={visibleColumns}
            dataSource={filteredData.map((s) => ({ ...s, key: s.id }))}
            pagination={{
              current: page, pageSize, total,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
              showSizeChanger: true, size: 'small',
              showTotal: (t) => `${t} submissions`,
              pageSizeOptions: ['20', '50', '100'],
            }}
            size="small" bordered scroll={{ x: 1700 }}
            rowClassName={(record) =>
              record.status === 'on_hold' ? 'ant-table-row-hold'
                : record.status === 'rejected' ? 'ant-table-row-rejected' : ''}
            expandable={{
              // Held rows expand to show the full hold note without truncation
              expandedRowRender: (record) => (
                <div style={{ padding: '8px 12px', background: T.holdSoft, border: `1px solid ${T.holdLine}`, borderRadius: 4 }}>
                  <Text strong style={{ fontSize: 12, color: T.hold, display: 'block', marginBottom: 4 }}>
                    On hold with Accounts
                  </Text>
                  <Text style={{ fontSize: 12, color: T.ink, whiteSpace: 'normal' }}>
                    {record.hold_remarks || record.accounts_remarks}
                  </Text>
                  <Text style={{ fontSize: 11, color: T.muted, display: 'block', marginTop: 4 }}>
                    Held by {record.held_by ? record.held_by.split('@')[0] : 'accounts'} on {formatDateTime(record.held_at)}
                  </Text>
                </div>
              ),
              rowExpandable: (record) => record.status === 'on_hold',
              showExpandColumn: heldOnPage.length > 0,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SubmissionsList;